import {Util} from 'yougile-util'
import {HubProperties} from 'yougile-model'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {GroupChatsClient} from '@client/group-chats-client'
import {Sync} from '@client/sync'
import {crmStore} from '@components/crm/crm-store'
import {ChatUtils} from '@extensions/chat-utils'
import {Company} from '@extensions/company'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import {GroupChatProxy} from '@extensions/proxies/group-chat-proxy'
import {PersonalChatProxy} from '@extensions/proxies/personal-chat-proxy'
import {ProjectProxy} from '@extensions/proxies/project-proxy'
import {Sticker} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {MainMenuCompany, MainMenuUsersAndGroup} from '../main-menu-store'

interface AllMyTasks {
  myTasksCount: number
  myTasksUnseenCount: number
}

interface CompanyPreferencesForCurrentUser {
  canCreateGroupChat: boolean
  canSeeLicensesPage: boolean
  canSeeStatsPage: boolean
}

export const getProjects = (): ProjectProxy[] => {
  return Project.listProjects(globals.getCompanyId(), 'not-archive', true)
    .sort(Project.projectOrderComparer)
    .map(ProjectProxy.create)
}

export const getCompanies = (): MainMenuCompany[] => {
  const user = Sync.getCurrentUser()
  const order = (company: Company): number => {
    const ix = user.companyOrder.indexOf(company.id)
    return ix > -1 ? ix : 1000
  }
  const sort = (a: Company, b: Company): number => order(a) - order(b)
  return Company.list()
    .sort(sort)
    .map(c => ({
      ...c,
      newMsg: ChatUtils.allNewMessagesInOtherCompany(c.id)
    }))
}

export const isDesktopBannerAllowed = (): boolean => {
  const profile = Sync.getProfile()
  if (Util.isDesktop()) {
    return false
  }
  const profileHub = profile.get(profile.companyId)
  const prop = profileHub.properties
  return (
    !prop[HubProperties.desktopAppHintDisabled] &&
    !prop[HubProperties.usedDesktopApp]
  )
}

export const isMobileBannerAllowed = (): boolean => {
  const profile = Sync.getProfile()
  const profileHub = profile.get(profile.companyId)
  const prop = profileHub.properties
  return (
    !prop[HubProperties.mobileAppHintDisabled] &&
    !prop[HubProperties.usedAndroidApp]
  )
}

export const allMyTasks = (): AllMyTasks => {
  const allMyTasks = Task.allTasks(globals.getCompanyId(), 'ignore-departments')
    .filter(
      t =>
        Sticker.getUsers(t).includes(globals.currentUserId) &&
        !Task.isArchived(t) &&
        !crmStore.isDeal(t)
    )
    .map(t => ({stage: Task.getGTDStage(t)}))

  return {
    myTasksCount: allMyTasks.filter(t => t.stage !== 'done').length,
    myTasksUnseenCount: allMyTasks.filter(t => !t.stage).length
  }
}

export const getUsersAndGroups = (): MainMenuUsersAndGroup => {
  const box = Sync.getCurrentSandbox()
  const users = box ? box.getAllUsers(false) : []

  const time = (c: PersonalChatProxy | GroupChatProxy): number =>
    c.lastMessageTimestamp || 0
  const name = (c: PersonalChatProxy | GroupChatProxy): string =>
    'users' in c ? c.chatName : c.userName

  const personalChatProxies = users
    .filter(u => !!u && u.id !== globals.currentUserId)
    .map(PersonalChatProxy.createForCurrentUser)
  const groupChatProxies = GroupChatsClient.listChats(
    box,
    globals.currentUserId
  ).map(h =>
    GroupChatProxy.create(box, h, {
      ignoreUnreadIfNotNotified: true
    })
  )

  return [...personalChatProxies, ...groupChatProxies].sort(
    (a, b) =>
      time(b) - time(a) ||
      (name(b).toLowerCase() > name(a).toLowerCase() ? 1 : -1)
  )
}

export const getUserRegisterTime = (): number => {
  const profile = Sync.getProfile()
  return profile.get(profile.companyId).timestamp
}

export const getCompanyPreferencesForCurrentUser =
  (): CompanyPreferencesForCurrentUser => {
    const box = Sync.getCurrentSandbox()
    const canCreateGroupChat = CompanyPreferencesClient.isAllowedForCurrentUser(
      box.companyId,
      'createGroupChat'
    )
    const canSeeLicensesPage = CompanyPreferencesClient.isAllowedForCurrentUser(
      box.companyId,
      'seeLicensePage'
    )
    const canSeeStatsPage = CompanyPreferencesClient.isAllowedForCurrentUser(
      box.companyId,
      'seeStatsPage'
    )
    return {
      canCreateGroupChat,
      canSeeLicensesPage,
      canSeeStatsPage
    }
  }
